import { computed, ComputedRef, ref, Ref } from 'vue'

import { ReviewEntryDetail } from '@/domain/entities/review/reviewEntryDetail'
import { CloseReviewEntryViewModel } from '@/presentation/viewmodels/review/closeReviewEntryViewModel'
import { OpenReviewEntryViewModel } from '@/presentation/viewmodels/review/openReviewEntryViewModel'
import { ReviewEntryDetailViewModel } from '@/presentation/viewmodels/review/reviewEntryDetailViewModel'
import { ReviewPageStateViewModel } from '@/presentation/viewmodels/review/reviewPageStateViewModel'

export class ReviewPresenter {
  private reviewPageStateViewModel: ReviewPageStateViewModel
  private reviewEntryDetailViewModel: ReviewEntryDetailViewModel
  private openReviewEntryViewModel: OpenReviewEntryViewModel
  private closeReviewEntryViewModel: CloseReviewEntryViewModel
  private loading: Ref<boolean>

  constructor(
    reviewPageStateViewModel: ReviewPageStateViewModel,
    reviewEntryDetailViewModel: ReviewEntryDetailViewModel,
    openReviewEntryViewModel: OpenReviewEntryViewModel,
    closeReviewEntryViewModel: CloseReviewEntryViewModel
  ) {
    this.reviewPageStateViewModel = reviewPageStateViewModel
    this.reviewEntryDetailViewModel = reviewEntryDetailViewModel
    this.openReviewEntryViewModel = openReviewEntryViewModel
    this.closeReviewEntryViewModel = closeReviewEntryViewModel
    this.loading = ref(false)
  }

  get isLoading(): Ref<boolean> {
    return this.loading
  }

  async initState(userId: string, problemId: string): Promise<void> {
    this.reviewPageStateViewModel.setEntrySummaryExpandedList({})
    this.reviewPageStateViewModel.setEntrySelectedReviewIndexList({})
    this.reviewEntryDetailViewModel.setReviewEntryDetailList({})
    await Promise.all([
      this.openReviewEntryViewModel.fetchReviewEntryList(userId, problemId),
      this.closeReviewEntryViewModel.fetchReviewEntryList(userId, problemId)
    ])
  }

  async postCloseEntry(entryId: string): Promise<void> {
    await this.reviewEntryDetailViewModel.postCloseEntry(entryId)
  }

  async postEvaluationReview(reviewId: string, score: number): Promise<void> {
    await this.reviewEntryDetailViewModel.postEvaluationReview(reviewId, score)
  }

  async clickEntrySummary(entryId: string): Promise<void> {
    if (!this.reviewEntryDetailViewModel.isAlreadyDetailRequested(entryId)) {
      this.loading.value = true
      await this.reviewEntryDetailViewModel.fetchReviewEntryDetailList(entryId)
      this.loading.value = false
    }
    this.reverseEntrySummaryExpandedFlag(entryId)
  }

  getEntryDetail(entryId: string): ComputedRef<ReviewEntryDetail> {
    return computed(() => {
      const index = this.reviewPageStateViewModel.entrySelectedReviewIndexList.value[entryId]
      const detailList = this.reviewEntryDetailViewModel.reviewEntryDetailList.value[entryId]
      return detailList?.[index] ?? this.reviewEntryDetailViewModel.getEmptyEntryDetail()
    })
  }

  isAiReview(entryId: string): ComputedRef<boolean> {
    return computed(() => {
      const reviewerId = this.getEntryDetail(entryId).value.reviewerId
      return [
        'gpt-3.5-turbo',
        'gpt-3.5_turbo',
        'gemini-pro',
        'gemini_pro',
        'gpt_4o',
        'gemini_25_flash',
        'gpt_oss_120b'
      ].includes(reviewerId)
    })
  }

  private reverseEntrySummaryExpandedFlag(entryId: string): void {
    const currentFlag = this.reviewPageStateViewModel.entrySummaryExpandedList.value[entryId]
    this.reviewPageStateViewModel.setEntrySummaryExpandedFlag(entryId, !currentFlag)
  }
}
